import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'

import { obtenerNovedades, type Novedad } from '../api/consultas'
import { aMilisegundos, comoDiaRelativo, comoHora } from '../lib/fechas'
import Hoja from './Hoja'

/** Mismo criterio que el punto del boton: lo de la ultima semana es "nuevo". */
export const DIAS_NUEVO = 7

export function hayNovedadesSinLeer(novedades: Novedad[] | undefined): boolean {
  const desde = Date.now() - DIAS_NUEVO * 86400000
  return (novedades ?? []).some((n) => !n.isRead && aMilisegundos(n.uploadDate) >= desde)
}

export function useNovedades() {
  return useQuery({
    queryKey: ['novedades'],
    queryFn: () => obtenerNovedades(60),
    staleTime: 5 * 60 * 1000,
  })
}

interface Entrada {
  manga: Novedad['manga']
  capitulos: Novedad[]
  ms: number
}

/*
 * Un dia, y dentro de el una entrada por serie.
 *
 * Lo de agrupar por serie no es cosmetica. Hay fuentes que no traen la fecha
 * real de cada capitulo y sellan el catalogo entero con la hora en que lo
 * importaste: LeerCapitulo pone la misma marca en los 1235 capitulos viejos
 * de One Piece. Sin agrupar, esa avalancha tapa las novedades de verdad de
 * las demas series.
 */
function agrupar(novedades: Novedad[]): [string, Entrada[]][] {
  const dias = new Map<string, Map<number, Entrada>>()

  for (const novedad of novedades) {
    const ms = aMilisegundos(novedad.uploadDate)
    if (!ms) continue
    const dia = comoDiaRelativo(ms)

    let series = dias.get(dia)
    if (!series) {
      series = new Map()
      dias.set(dia, series)
    }

    const entrada = series.get(novedad.manga.id)
    if (entrada) {
      entrada.capitulos.push(novedad)
      entrada.ms = Math.max(entrada.ms, ms)
    } else {
      series.set(novedad.manga.id, { manga: novedad.manga, capitulos: [novedad], ms })
    }
  }

  return [...dias.entries()].map(([dia, series]) => [
    dia,
    [...series.values()].sort((a, b) => b.ms - a.ms),
  ])
}

function Fila({ entrada, alCerrar }: { entrada: Entrada; alCerrar: () => void }) {
  const sinLeer = entrada.capitulos.filter((c) => !c.isRead).length
  const solo = entrada.capitulos.length === 1 ? entrada.capitulos[0] : null

  // Con un solo capitulo se va derecho a leerlo; con varios, a la ficha, que
  // es donde se elige por cual seguir.
  const destino = solo ? `/leer/${solo.id}` : `/manga/${entrada.manga.id}`

  const detalle = solo
    ? [solo.name, solo.scanlator].filter(Boolean).join(' · ')
    : `${entrada.capitulos.length} capitulos` + (sinLeer ? ` · ${sinLeer} sin leer` : '')

  return (
    <li>
      <Link to={destino} onClick={alCerrar} className={sinLeer ? undefined : 'leido'}>
        <span style={{ flex: 1 }}>
          {entrada.manga.title}
          <span className="meta">{detalle}</span>
        </span>
        <span className="novedades-hora">
          {comoHora(entrada.ms)}
          {sinLeer > 0 && <span className="punto-nuevo" />}
        </span>
      </Link>
    </li>
  )
}

export default function Novedades({ alCerrar }: { alCerrar: () => void }) {
  const { data, isLoading, error } = useNovedades()

  const dias = useMemo(() => agrupar(data ?? []), [data])

  return (
    <Hoja
      titulo="Novedades"
      nota="Capitulos nuevos de las series de tu biblioteca"
      alCerrar={alCerrar}
    >
      {isLoading && <p className="cargando">Cargando…</p>}

      {error && (
        <div className="aviso error">
          <p>{(error as Error).message}</p>
        </div>
      )}

      {data && data.length === 0 && (
        <p className="meta" style={{ marginTop: 14 }}>
          Todavia no hay nada. Aqui apareceran los capitulos nuevos de las series que tengas
          guardadas en la biblioteca.
        </p>
      )}

      {dias.map(([dia, entradas]) => (
        <div key={dia} className="novedades-dia">
          <h3>{dia}</h3>
          <ul className="lista">
            {entradas.map((entrada) => (
              <Fila key={entrada.manga.id} entrada={entrada} alCerrar={alCerrar} />
            ))}
          </ul>
        </div>
      ))}
    </Hoja>
  )
}
